import { IButtonBaseStyle, IButtonWidgetBaseStyle, ICalendarAppointmentScheduleWidgetData } from "@pageBuilder/typings";
import { CssProperties } from "../../models/css-property";
import { WidgetRendererBase } from "./widget-renderer-base";
import { StringUtil } from "../../utils/string-util";

export class CalendarAppointmentScheduleWidgetRenderer extends WidgetRendererBase<ICalendarAppointmentScheduleWidgetData> {

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("calendarAppointmentScheduleWidget");
    }

    protected renderInternal(element: HTMLElement, data: ICalendarAppointmentScheduleWidgetData): HTMLElement {
        this.renderButtonElement(element, data);
        this.renderStyles(element, data);

        return element;
    }

    private renderButtonElement(element: HTMLElement, data: ICalendarAppointmentScheduleWidgetData): void {
        const existButton = element.querySelector(".button-element");
        existButton?.remove();

        const label = document.createElement("span");
        label.classList.add("label");
        label.textContent = data.label;
        label.dataset.placeholder = data.labelPlaceholder;

        const btn = document.createElement("button");
        btn.ariaLabel = data.label ?? "button";
        btn.classList.add("button-element");

        btn.classList.toggle("secondary", data.style?.buttonStyle?.theme === "secondary");

        btn.setAttribute("tabindex", "-1");
        btn.appendChild(label);

        const content = element.querySelector(".widget-content") as HTMLElement;
        content.appendChild(btn);
    }

    protected renderStyles(element: HTMLElement, data: ICalendarAppointmentScheduleWidgetData): void {
        this.renderBaseStyles(element, data.style);
        this.renderButtonStyles(element, data.style?.buttonStyle);
    }

    private renderBaseStyles(element: HTMLElement, style?: IButtonWidgetBaseStyle | null): void {
        if (!style) {
            this.cleanupStyle(element);
            return;
        }

        this.animationRenderer.setAnimationStyles(element, style?.animation);
        const elementHeight = style.heightData?.height ? `${style.heightData.height}px` : "auto";
        element.style.height = elementHeight;
        element.style.minHeight = elementHeight;
    }

    private renderButtonStyles(element: HTMLElement, style?: IButtonBaseStyle | null): void {
        if (!style) {
            this.cleanupButtonStyles(element);
            return;
        }

        this.setButtonStyles(element, style);
        this.setWidgetAlignment(element, style);
    }

    private setButtonStyles(element: HTMLElement, style: IButtonBaseStyle): void {
        const cssProperties = new CssProperties();

        if (style?.font?.size) {
            cssProperties.tryAdd("--font-size", String(StringUtil.getNumberFromStr(style.font.size)));
        }
        cssProperties.tryAdd("--font-family", style.font?.family ?? "");
        cssProperties.tryAdd("--font-height", style.font?.lineHeight ?? "");
        cssProperties.tryAdd("--font-weight", style.font?.weight ?? "");
        cssProperties.tryAdd("--color", style.color ?? "");
        cssProperties.tryAdd("--hovered-color", style.hoverColor ?? "");
        cssProperties.tryAdd("--border-color", style.borderColor ?? "");
        cssProperties.tryAdd("--hovered-border-color", style.borderHoverColor ?? "");

        cssProperties.tryAdd("border-width", style.thickness);
        if (style.borderRadius) {
            cssProperties.tryAdd("border-top-left-radius", `${style.borderRadius.topLeftPx}px`);
            cssProperties.tryAdd("border-top-right-radius", `${style.borderRadius.topRightPx}px`);
            cssProperties.tryAdd("border-bottom-left-radius", `${style.borderRadius.bottomLeftPx}px`);
            cssProperties.tryAdd("border-bottom-right-radius", `${style.borderRadius.bottomRightPx}px`);
        }

        cssProperties.tryAdd("--background-color", style.backgroundColor ?? "");
        cssProperties.tryAdd("--hovered-background-color", style.backgroundHoverColor ?? "");

        if (style.fitMode) {
            const isContainMode = style.fitMode === "contain";
            cssProperties.tryAdd("width", isContainMode ? "auto" : "100%");
        }

        if (this.isEditorRendering) {
            cssProperties.tryAdd("pointer-events", "none");
        }

        this.styleService.setStyle(element, `#${element.id} .button-element`, cssProperties.data);
    }

    public setWidgetAlignment(element: HTMLElement, style: IButtonBaseStyle): void {
        const positionStyles = new CssProperties();

        if (style.buttonAlignment) {
            positionStyles.tryAdd("justify-content", style.buttonAlignment);
        }

        this.styleService.setStyle(element, `#${element.id} .widget-content`, positionStyles.data);
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`, `#${element.id} .button-element`, `#${element.id} .button-element:hover`, `#${element.id} .button-element:not(:hover)`, `#${element.id} .widget-content`];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        //nothing
    }

    private cleanupStyle(element: HTMLElement): void {
        this.styleService.clearStyle(element, `#${element.id}`);
        this.cleanupButtonStyles(element);
    }

    private cleanupButtonStyles(element: HTMLElement): void {
        this.styleService.clearStyle(element, `#${element.id} .button-element`);
    }

}
